import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  AppIcon,
  Badge,
  Button,
  Dialog,
  DialogClose,
  IconClock,
  IconPencil,
  IconPlus,
  IconTrash,
  ListTable,
  Notice,
  Plate,
  SelectField,
  TabPanel,
  Tabs,
  TextField,
  type ListColumn,
} from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { ConfirmDialog, type Confirm } from "../ConfirmDialog.tsx";
import { formatNumber, t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { attendanceApi, type AttendanceSettings, type Kiosk, type Shift, type Timesheet } from "./api.ts";
import { DayField, useActiveEmployees, useNames } from "./common.tsx";
import { historyQuery, kiosksQuery, overtimeQuery, PEOPLE, settingsQuery, shiftsQuery, timesheetQuery } from "./queries.ts";
import { addDays, formatMonth, hm, instant, localDate, localTime, minutesBetween } from "./time.ts";

export type AttendanceTab = "marks" | "timesheet" | "settings";

const route = getRouteApi("/app/attendance");

/** «Відвідування»: отметки дня с правкой и переработкой, «Табель за місяць», настройки и киоски. */
export function AttendancePage() {
  const { tab = "marks", date, month } = route.useSearch();
  const navigate = useNavigate();
  const day = date ?? localDate();
  const go = (s: { tab?: AttendanceTab; date?: string; month?: string }) =>
    void navigate({ to: "/attendance", search: { tab, date, month, ...s }, replace: true });
  return (
    <div className="page people">
      <h1 className="page-title page-title-icon">
        <AppIcon name="attendance" size={32} />
        {t("section.attendance")}
      </h1>
      <Tabs
        label={t("section.attendance")}
        value={tab}
        onValueChange={(v) => go({ tab: v })}
        items={[
          { value: "marks", label: t("att.tab.marks") },
          { value: "timesheet", label: t("att.tab.timesheet") },
          { value: "settings", label: t("att.tab.settings") },
        ]}
      >
        <TabPanel value="marks">
          <MarksTab day={day} onDay={(d) => go({ date: d })} />
        </TabPanel>
        <TabPanel value="timesheet">
          <TimesheetTab month={month} onMonth={(m) => go({ month: m })} />
        </TabPanel>
        <TabPanel value="settings">
          <SettingsTab />
        </TabPanel>
      </Tabs>
    </div>
  );
}

// --- Відмітки ----------------------------------------------------------------

/**
 * Отметки дня по сотрудникам (ATT-01, КД-04): правка менеджером пишется в
 * историю с автором. Под сотрудником переработка дня (H13, КД-05):
 * сверх часов графика с допуском, подтверждается на текущие часы.
 */
function MarksTab({ day, onDay }: { day: string; onDay: (d: string) => void }) {
  const shifts = useQuery(shiftsQuery(day));
  const name = useNames();
  const [edit, setEdit] = useState<{ shift: Shift | null } | null>(null);
  const [history, setHistory] = useState<Shift | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const queryClient = useQueryClient();
  const remove = useMutation({ mutationFn: attendanceApi.remove });

  const byEmployee = new Map<string, Shift[]>();
  for (const s of shifts.data ?? []) byEmployee.set(s.employeeId, [...(byEmployee.get(s.employeeId) ?? []), s]);
  const groups = [...byEmployee.entries()].sort(([a], [b]) => name(a).localeCompare(name(b), "uk"));

  return (
    <div className="people-tab">
      <div className="people-toolbar">
        <DayField value={day} onChange={onDay} />
        <Button variant="create" onClick={() => setEdit({ shift: null })}>
          <IconPlus />
          {t("att.add")}
        </Button>
      </div>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {shifts.error ? <Notice tone="danger" title={errorNotice(shifts.error).title} className="stock-notice" /> : null}
      {shifts.data && groups.length === 0 ? <p className="w-muted people-empty">{t("att.empty")}</p> : null}
      <ul className="people-groups">
        {groups.map(([employeeId, list]) => (
          <li key={employeeId}>
            <Plate cut={false} className="people-group">
              <div className="people-group-head">
                <span className="people-group-name">{name(employeeId)}</span>
              </div>
              <ul className="people-shifts">
                {list.map((s) => (
                  <li key={s.id} className="people-shift">
                    <span className="people-shift-time w-num">
                      {shiftTime(s, day)}
                    </span>
                    <span className="people-shift-meta">
                      <Badge tone={s.source === "manual" ? "info" : undefined}>{t(`att.source.${sourceKey(s.source)}`)}</Badge>
                      {s.checkOut ? <span className="w-muted w-num">{hm(minutesBetween(s.checkIn, s.checkOut))}</span> : <Badge tone="warning">{t("att.open")}</Badge>}
                    </span>
                    <span className="people-shift-actions">
                      <Button variant="ghost" size="icon" aria-label={t("att.edit")} onClick={() => setEdit({ shift: s })}>
                        <IconPencil />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label={t("att.history")} onClick={() => setHistory(s)}>
                        <IconClock />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={t("att.delete")}
                        onClick={() =>
                          setConfirm({
                            title: t("att.deleteTitle"),
                            body: t("att.deleteBody", { name: name(s.employeeId), time: shiftTime(s, day) }),
                            action: t("att.delete"),
                            run: () =>
                              remove.mutate(s.id, {
                                onSuccess: () => void queryClient.invalidateQueries({ queryKey: PEOPLE }),
                                onError: (err) => setNotice(errorNotice(err)),
                              }),
                          })
                        }
                      >
                        <IconTrash />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
              <OvertimeLine employeeId={employeeId} day={day} onNotice={setNotice} />
            </Plate>
          </li>
        ))}
      </ul>
      {edit ? <ShiftDialog shift={edit.shift} day={day} onClose={() => setEdit(null)} /> : null}
      <HistoryDialog shift={history} onClose={() => setHistory(null)} />
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

const sourceKey = (s: string) => (["kiosk_badge", "kiosk_pin", "manual", "terminal"].includes(s) ? (s as "kiosk_badge" | "kiosk_pin" | "manual" | "terminal") : "manual");

/** «08:00 – 17:10»; уход в другой день с датой, открытая смена с тире. */
function shiftTime(s: Shift, day: string): string {
  const out = s.checkOut ? localTime(s.checkOut) + (localDate(Date.parse(s.checkOut)) !== day ? ` (${localDate(Date.parse(s.checkOut)).slice(5).split("-").reverse().join(".")})` : "") : "…";
  const inDay = localDate(Date.parse(s.checkIn));
  const start = localTime(s.checkIn) + (inDay !== day ? ` (${inDay.slice(5).split("-").reverse().join(".")})` : "");
  return `${start} – ${out}`;
}

function OvertimeLine({ employeeId, day, onNotice }: { employeeId: string; day: string; onNotice: (n: NoticeState | null) => void }) {
  const overtime = useQuery(overtimeQuery(employeeId, day));
  const queryClient = useQueryClient();
  const confirm = useMutation({ mutationFn: () => attendanceApi.confirm(employeeId, day) });
  const o = overtime.data;
  if (!o) return null;
  return (
    <div className="people-overtime">
      <span className="w-muted">
        {t("att.worked", { worked: hm(o.workedMinutes), scheduled: hm(o.scheduledMinutes) })}
      </span>
      {o.overtimeMinutes > 0 ? (
        <span className="people-overtime-value">
          <span className="w-num">{t("att.overtime", { value: hm(o.overtimeMinutes) })}</span>
          {o.pending ? (
            <Button
              disabled={confirm.isPending}
              onClick={() => {
                onNotice(null);
                confirm.mutate(undefined, {
                  onSuccess: () => void queryClient.invalidateQueries({ queryKey: PEOPLE }),
                  onError: (err) => onNotice(errorNotice(err)),
                });
              }}
            >
              {t("att.confirm")}
            </Button>
          ) : (
            <Badge tone="success">{t("att.confirmed")}</Badge>
          )}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Новая отметка или правка прихода и ухода. Время местное, день экрана;
 * уход раньше прихода значит следующий день (ночная смена).
 */
function ShiftDialog({ shift, day, onClose }: { shift: Shift | null; day: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const employees = useActiveEmployees();
  const name = useNames();
  const baseDay = shift ? localDate(Date.parse(shift.checkIn)) : day;
  const [employeeId, setEmployeeId] = useState(shift?.employeeId ?? "");
  const [checkIn, setCheckIn] = useState(shift ? localTime(shift.checkIn) : "08:00");
  const [checkOut, setCheckOut] = useState(shift?.checkOut ? localTime(shift.checkOut) : shift ? "" : "17:00");
  const [notice, setNotice] = useState<NoticeState | null>(null);

  const inAt = instant(baseDay, checkIn);
  const outAt = checkOut ? instant(checkOut <= checkIn ? addDays(baseDay, 1) : baseDay, checkOut) : null;

  const save = useMutation({
    mutationFn: async () => {
      if (!shift) return attendanceApi.create(employeeId, inAt, outAt);
      // Две правки, если поменяли оба: в истории каждая своей строкой.
      if (inAt !== shift.checkIn) await attendanceApi.checkIn(shift.id, inAt);
      if (outAt !== (shift.checkOut ?? null)) await attendanceApi.checkOut(shift.id, outAt);
      return shift;
    },
  });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    save.mutate(undefined, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: PEOPLE });
        onClose();
      },
      onError: (err) => {
        void queryClient.invalidateQueries({ queryKey: PEOPLE });
        setNotice(errorNotice(err));
      },
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={shift ? t("att.editTitle", { name: name(shift.employeeId) }) : t("att.add")}
      description={t("att.editHint")}
      footer={
        <>
          <Button variant="primary" type="submit" form="shift-form" disabled={save.isPending || (!shift && !employeeId)}>
            {t("order.save")}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">{t("common.cancel")}</Button>
          </DialogClose>
        </>
      }
    >
      <form id="shift-form" className="stock-form" onSubmit={submit}>
        {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
        {shift ? null : (
          <SelectField
            label={t("att.employee")}
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            options={[{ value: "", label: t("att.employeePick"), disabled: true }, ...employees.map((x) => ({ value: x.id, label: x.name }))]}
          />
        )}
        <div className="people-pair">
          <TextField label={t("att.checkIn")} type="time" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} required />
          <TextField label={t("att.checkOut")} hint={t("att.checkOutHint")} type="time" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
        </div>
      </form>
    </Dialog>
  );
}

const editTime = (iso: string | null | undefined) => (iso ? `${localDate(Date.parse(iso)).slice(5).split("-").reverse().join(".")} ${localTime(iso)}` : "—");

/** История правок отметки: кто, когда, что было и что стало (КД-04). */
function HistoryDialog({ shift, onClose }: { shift: Shift | null; onClose: () => void }) {
  const history = useQuery({ ...historyQuery(shift?.id ?? 0), enabled: shift !== null });
  return (
    <Dialog
      open={shift !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={t("att.history")}
      footer={
        <DialogClose asChild>
          <Button variant="primary">{t("people.close")}</Button>
        </DialogClose>
      }
    >
      {history.error ? <Notice tone="danger" title={errorNotice(history.error).title} /> : null}
      <ol className="people-history">
        {history.data?.map((h, i) => (
          <li key={i}>
            <span className="w-num">{editTime(h.at)}</span>
            <span>
              {t(`att.field.${fieldKey(h.field)}`)}: <span className="w-num">{editTime(h.before)}</span> → <span className="w-num">{editTime(h.after)}</span>
            </span>
            <span className="w-muted">{t("att.by", { user: h.by.slice(0, 8) })}</span>
          </li>
        ))}
      </ol>
    </Dialog>
  );
}

const fieldKey = (f: string) => (["created", "check_in", "check_out", "deleted"].includes(f) ? (f as "created" | "check_in" | "check_out" | "deleted") : "created");

// --- Табель за місяць --------------------------------------------------------

type Row = Timesheet["rows"][number];

// Часы табеля с двумя знаками, как их считает сервер: 7,50, а не 7,5.
const hoursFormat = new Intl.NumberFormat("uk", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hours = (n: number) => hoursFormat.format(n);

/** CSV для бухгалтерии: разделитель «;», дробь запятой, как ждёт Excel по-украински. */
function csv(sheet: Timesheet): string {
  const num = (n: number) => String(n).replace(".", ",");
  const head = [t("people.name"), t("att.ts.worked"), t("att.ts.scheduled"), t("att.ts.overtime"), t("att.ts.confirmed"), t("att.ts.payable"), t("att.ts.confirmedPayable")];
  const lines = sheet.rows.map((r) => [`"${r.name.replace(/"/g, '""')}"`, num(r.worked), num(r.scheduled), num(r.overtime), num(r.confirmedOvertime), num(r.overtimePayable), num(r.confirmedPayable)].join(";"));
  return "﻿" + [head.join(";"), ...lines].join("\r\n");
}

/** «Табель за місяць» (ATT-03, КД-07): по умолчанию прошлый месяц, часы с двумя знаками. */
function TimesheetTab({ month, onMonth }: { month: string | undefined; onMonth: (m: string) => void }) {
  const sheet = useQuery(timesheetQuery(month));
  const s = sheet.data;
  const columns: ListColumn<Row>[] = [
    { key: "name", header: t("people.name"), kind: "title", render: (r) => r.name },
    { key: "worked", header: t("att.ts.worked"), kind: "number", width: "7rem", render: (r) => hours(r.worked) },
    { key: "scheduled", header: t("att.ts.scheduled"), kind: "number", width: "7rem", render: (r) => hours(r.scheduled) },
    { key: "overtime", header: t("att.ts.overtime"), kind: "number", width: "7rem", render: (r) => hours(r.overtime) },
    { key: "confirmed", header: t("att.ts.confirmed"), kind: "number", width: "7rem", render: (r) => hours(r.confirmedOvertime) },
    { key: "payable", header: t("att.ts.confirmedPayable"), kind: "number", width: "7rem", render: (r) => hours(r.confirmedPayable) },
  ];
  const download = () => {
    if (!s) return;
    const url = URL.createObjectURL(new Blob([csv(s)], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `tabel-${s.month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="people-tab">
      <div className="people-toolbar">
        <TextField label={t("att.ts.month")} type="month" value={s?.month ?? month ?? ""} onChange={(e) => e.target.value && onMonth(e.target.value)} className="people-month" />
        <Button disabled={!s || s.rows.length === 0} onClick={download}>
          {t("att.ts.download")}
        </Button>
      </div>
      {sheet.error ? <Notice tone="danger" title={errorNotice(sheet.error).title} className="stock-notice" /> : null}
      {s ? (
        <p className="w-muted people-caption">
          {t("att.ts.caption", { month: formatMonth(s.month), rate: formatNumber(s.rate) })}
        </p>
      ) : null}
      <ListTable label={t("att.tab.timesheet")} columns={columns} rows={s?.rows ?? []} rowKey={(r) => r.employeeId} empty={s ? t("att.ts.empty") : null} />
    </div>
  );
}

// --- Налаштування ------------------------------------------------------------

function SettingsTab() {
  const settings = useQuery(settingsQuery);
  return (
    <div className="people-tab stock-narrow">
      {settings.error ? <Notice tone="danger" title={errorNotice(settings.error).title} className="stock-notice" /> : null}
      {settings.data ? <SettingsForm value={settings.data} /> : null}
      <Kiosks />
    </div>
  );
}

/** Настройки один раз (SHL-13): умолчания ставит сервер, дальше решает владелец (КД-06). */
function SettingsForm({ value }: { value: AttendanceSettings }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(value);
  const [rate, setRate] = useState(String(value.overtimeRate).replace(".", ","));
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const save = useMutation({ mutationFn: attendanceApi.saveSettings });
  const set = <K extends keyof AttendanceSettings>(k: K, v: AttendanceSettings[K]) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    save.mutate(
      { ...form, overtimeRate: Number(rate.replace(",", ".")) },
      {
        onSuccess: (saved) => {
          queryClient.setQueryData(settingsQuery.queryKey, saved);
          void queryClient.invalidateQueries({ queryKey: PEOPLE });
          setNotice({ tone: "success", title: t("people.saved") });
        },
        onError: (err) => setNotice(errorNotice(err)),
      },
    );
  };
  return (
    <>
      <h2 className="people-h2">{t("att.settings")}</h2>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      <Plate cut="lg">
        <form className="stock-form" onSubmit={submit}>
          <label className="check">
            <input type="checkbox" checked={form.kioskPin} onChange={(e) => set("kioskPin", e.target.checked)} />
            {t("att.settings.kioskPin")}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.overtimeApproval} onChange={(e) => set("overtimeApproval", e.target.checked)} />
            {t("att.settings.approval")}
          </label>
          <label className="check">
            <input type="checkbox" checked={form.showOvertime} onChange={(e) => set("showOvertime", e.target.checked)} />
            {t("att.settings.show")}
          </label>
          <div className="people-pair">
            <TextField label={t("att.settings.rate")} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} />
            <TextField
              label={t("att.settings.tolerance")}
              hint={t("att.settings.toleranceHint")}
              type="number"
              min={0}
              inputMode="numeric"
              value={String(form.toleranceMinutes)}
              onChange={(e) => set("toleranceMinutes", Number(e.target.value))}
            />
          </div>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {t("order.save")}
          </Button>
        </form>
      </Plate>
    </>
  );
}

/**
 * Киоски у входа: токен устройства виден один раз, при создании. Его
 * вводят на планшете в «Кіоск»; потерянный планшет отзывается здесь.
 */
function Kiosks() {
  const kiosks = useQuery(kiosksQuery);
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [created, setCreated] = useState<Kiosk | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const create = useMutation({ mutationFn: attendanceApi.createKiosk });
  const revoke = useMutation({ mutationFn: attendanceApi.revokeKiosk });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: kiosksQuery.queryKey });
  return (
    <>
      <h2 className="people-h2">{t("kiosk.devices")}</h2>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {created?.token ? (
        <Notice tone="success" title={t("kiosk.created", { name: created.name })} className="stock-notice">
          <p>{t("kiosk.tokenOnce")}</p>
          <p className="people-token">{created.token}</p>
          <Link to="/kiosk">{t("kiosk.open")}</Link>
        </Notice>
      ) : null}
      <Plate cut="md">
        <form
          className="people-inline"
          onSubmit={(e) => {
            e.preventDefault();
            setNotice(null);
            create.mutate(name.trim(), {
              onSuccess: (k) => {
                setCreated(k);
                setName("");
                refresh();
              },
              onError: (err) => setNotice(errorNotice(err)),
            });
          }}
        >
          <TextField label={t("kiosk.name")} placeholder={t("kiosk.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          <Button type="submit" variant="create" disabled={create.isPending || !name.trim()}>
            {t("kiosk.add")}
          </Button>
        </form>
      </Plate>
      <ul className="people-list">
        {kiosks.data?.map((k) => (
          <li key={k.id} className="people-list-row">
            <span className="people-list-main">
              <span>{k.name}</span>
              <span className="w-muted">{k.createdAt ? t("kiosk.since", { date: localDate(Date.parse(k.createdAt)).split("-").reverse().join(".") }) : null}</span>
            </span>
            {k.revoked ? (
              <Badge>{t("kiosk.revoked")}</Badge>
            ) : (
              <Button
                variant="ghost"
                onClick={() =>
                  setConfirm({
                    title: t("kiosk.revokeTitle", { name: k.name }),
                    body: t("kiosk.revokeBody"),
                    action: t("kiosk.revoke"),
                    run: () => revoke.mutate(k.id, { onSuccess: refresh, onError: (err) => setNotice(errorNotice(err)) }),
                  })
                }
              >
                {t("kiosk.revoke")}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <ConfirmDialog confirm={confirm} onClose={() => setConfirm(null)} />
    </>
  );
}
